import React from 'react';
import { AIUsageStats, AIInsight } from '../../types';

interface TokenUsageProps {
  usageStats: AIUsageStats;
  currentInsight: AIInsight | null;
  onClearAllData: () => void;
}

const TokenUsage: React.FC<TokenUsageProps> = ({
  usageStats,
  currentInsight,
  onClearAllData,
}) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4
    }).format(amount);
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="token-usage-section">
      <div className="token-usage-header">
        <h3>💰 Token Usage & Billing</h3>
        <button 
          className="clear-data-button"
          onClick={onClearAllData}
          title="Clear all AI data and usage history"
        >
          Clear All Data
        </button>
      </div>

      {currentInsight && (
        <div className="current-session-stats">
          <h4>Current Session</h4>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Tasks Analyzed:</span>
              <span className="stat-value">{currentInsight.task_count}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Input Tokens:</span>
              <span className="stat-value">{formatNumber(currentInsight.token_usage.prompt_tokens)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Output Tokens:</span>
              <span className="stat-value">{formatNumber(currentInsight.token_usage.completion_tokens)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Tokens:</span>
              <span className="stat-value">{formatNumber(currentInsight.token_usage.total_tokens)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Session Cost:</span>
              <span className="stat-value cost">{formatCurrency(currentInsight.billing_info.total_cost)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Generated:</span>
              <span className="stat-value">{new Date(currentInsight.created_at).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      <div className="overall-stats">
        <h4>Overall Usage</h4>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">Total Sessions:</span>
            <span className="stat-value">{formatNumber(usageStats.total_sessions)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Tokens:</span>
            <span className="stat-value">{formatNumber(usageStats.total_tokens)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Cost:</span>
            <span className="stat-value cost">{formatCurrency(usageStats.total_cost)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">This Month:</span>
            <span className="stat-value cost">{formatCurrency(usageStats.current_month_cost)}</span>
          </div>
        </div>
      </div>

      <div className="pricing-info">
        <h4>Model Pricing (GPT-4.1-mini)</h4>
        <div className="pricing-grid">
          <div className="pricing-item">
            <span className="pricing-label">Input:</span>
            <span className="pricing-value">$0.40 / 1M tokens</span>
          </div>
          <div className="pricing-item">
            <span className="pricing-label">Cached Input:</span>
            <span className="pricing-value">$0.10 / 1M tokens</span>
          </div>
          <div className="pricing-item">
            <span className="pricing-label">Output:</span>
            <span className="pricing-value">$1.60 / 1M tokens</span>
          </div>
        </div>
      </div>

      {usageStats.insights.length > 1 && (
        <div className="insights-history">
          <h4>Recent Insights</h4>
          <div className="insights-list">
            {usageStats.insights.slice(-5).reverse().map((insight, index) => (
              <div key={insight.id} className="insight-summary">
                <div className="insight-meta">
                  <span className="insight-date">
                    {new Date(insight.created_at).toLocaleDateString()}
                  </span>
                  <span className="insight-cost">
                    {formatCurrency(insight.billing_info.total_cost)}
                  </span>
                </div>
                <div className="insight-details">
                  {insight.task_count} tasks, {formatNumber(insight.token_usage.total_tokens)} tokens
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenUsage; 